import { isEqual } from 'lodash';
import qs, { ParsedQs } from 'qs';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router';
import useDebouncedEffect from './useDebouncedEffect';

export const useQueryString = <T extends ParsedQs>(
	initialValues?: Partial<T>,
): [Partial<T>, <S extends keyof T>(name: S, value: T[S] | null) => void] => {
	const history = useHistory();

	const [isDirty, setDirty] = useState<boolean>(false);
	const [searchParams, setSearchParams] = useState<Partial<T>>(() => {
		const queryValues = qs.parse(window.location.search, { ignoreQueryPrefix: true }) as Partial<T>;
		return {
			...initialValues,
			...queryValues,
		};
	});

	const setSearchParam = useCallback(<S extends keyof T>(name: S, value: T[S] | null): void => {
		setDirty(true);
		setSearchParams((prev) => {
			if (!!value) {
				return { ...prev, [name]: value };
			} else {
				const newParams = { ...prev };
				delete newParams[name];
				return newParams;
			}
		});
	}, []);

	useDebouncedEffect(
		() => {
			if (!isDirty) return;
			history.replace({
				pathname: window.location.pathname,
				search: qs.stringify(searchParams, { addQueryPrefix: false }),
			});
		},
		500,
		[history, searchParams, isDirty],
	);

	return [searchParams, setSearchParam];
};
